import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  CalendarDays,
  Store,
  FileText,
  LogOut,
  User,
  Users,
  ChevronDown,
  Search,
  Home,
  Settings,
  QrCode,
  Trophy,
} from 'lucide-react';
import Logo from '../Logo';
import { apiClient } from '../../api/client';

interface StoreOption {
  id: string;
  name: string;
  logoUrl?: string | null;
}

// Admin navigation items (full admin view)
const adminNavItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/campaigns', label: 'Campaigns', icon: CalendarDays },
  { path: '/admin/stores', label: 'Participating Stores', icon: Store },
  { path: '/admin/customers', label: 'Customers', icon: Users },
  { path: '/admin/winners', label: 'Winners', icon: Trophy },
  { path: '/admin/audit-logs', label: 'Audit Logs', icon: FileText },
];

// Store view navigation items (when viewing as a store)
const storeNavItems = [
  { path: '/admin/dashboard', label: 'My Dashboard', icon: LayoutDashboard },
  { path: '/admin/campaigns', label: 'My Campaigns', icon: CalendarDays },
  { path: '/admin/qrcodes', label: 'My QR Codes', icon: QrCode },
  { path: '/admin/customers', label: 'My Customers', icon: Users },
  { path: '/admin/winners', label: 'My Winners', icon: Trophy },
  { path: '/admin/store-settings', label: 'Store Profile', icon: Settings },
  { path: '/admin/audit-logs', label: 'Activity Log', icon: FileText },
];

export default function AdminLayout() {
  const { user, logout, login } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [stores, setStores] = useState<StoreOption[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const viewAsStoreId = searchParams.get('viewAs');
  const viewAsStore = stores.find(s => s.id === viewAsStoreId);

  // Determine which nav items to show
  const navItems = viewAsStoreId ? storeNavItems : adminNavItems;

  // Handle token from magic link redirect
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      login(token);
      searchParams.delete('token');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, login]);

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadStores = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: StoreOption[] }>('/admin/stores');
      setStores(response.data.data);
    } catch (error) {
      console.error('Failed to load stores:', error);
    }
  };

  const handleStoreSelect = (storeId: string | null) => {
    if (storeId) {
      setSearchParams({ viewAs: storeId });
    } else {
      searchParams.delete('viewAs');
      setSearchParams(searchParams);
    }
    setIsDropdownOpen(false);
    setSearchQuery('');
  };

  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!user || !['SYSTEM_ADMIN', 'CO_ADMIN'].includes(user.role)) {
    return <Navigate to="/login" replace />;
  }

  // Build the link with viewAs param preserved
  const buildLink = (path: string) => {
    if (viewAsStoreId) {
      return `${path}?viewAs=${viewAsStoreId}`;
    }
    return path;
  };

  // Check if path is active (ignoring query params)
  const isPathActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px]">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200">
        <div className="flex flex-col h-full">
          {/* Logo / Store Header */}
          <Link
            to={buildLink('/admin/dashboard')}
            className="flex items-center gap-3 py-4 px-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
          >
            {viewAsStore ? (
              // Show store branding when viewing as store
              <>
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                  {viewAsStore.logoUrl ? (
                    <img src={viewAsStore.logoUrl} alt={viewAsStore.name} className="w-full h-full object-cover" />
                  ) : (
                    <Store className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-sm font-bold text-gray-900 leading-tight truncate">{viewAsStore.name}</h1>
                  <p className="text-xs text-amber-600 font-medium">Store View</p>
                </div>
              </>
            ) : (
              // Show app branding for admin view
              <>
                <Logo size="md" />
                <div>
                  <h1 className="text-sm font-bold text-gray-900 leading-tight">Discover The Park</h1>
                  <p className="text-xs text-gray-500">Activity Planner</p>
                </div>
              </>
            )}
          </Link>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = isPathActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={buildLink(item.path)}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className={`w-5 h-5 mr-3 ${isActive ? 'text-primary-600' : 'text-gray-400'}`} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Main content */}
      <main className="pl-64">
        {/* Top header bar */}
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-8 py-3 flex justify-between items-center">
          {/* View as Store indicator */}
          {viewAsStore && (
            <div className="flex items-center gap-2 bg-amber-50 text-amber-800 px-3 py-1 rounded-lg text-sm">
              <Store className="w-4 h-4" />
              <span>Viewing as: <strong>{viewAsStore.name}</strong></span>
            </div>
          )}
          {!viewAsStore && <div />}

          {/* Right side: Store switcher + User info */}
          <div className="flex items-center gap-4">
            {/* Store switcher dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewAsStore
                    ? 'bg-amber-100 hover:bg-amber-200 text-amber-800'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                <Store className="w-4 h-4" />
                <span>{viewAsStore ? 'Switch Store' : 'View as Store'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                  {/* Search input */}
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search stores..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Options list */}
                  <div className="max-h-64 overflow-y-auto">
                    {/* Admin Home option */}
                    <button
                      onClick={() => handleStoreSelect(null)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                        !viewAsStoreId ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                      }`}
                    >
                      <Home className="w-4 h-4" />
                      <span className="font-medium">Admin Home</span>
                    </button>

                    <div className="border-t border-gray-100" />

                    {/* Store options */}
                    {filteredStores.length > 0 ? (
                      filteredStores.map((store) => (
                        <button
                          key={store.id}
                          onClick={() => handleStoreSelect(store.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                            viewAsStoreId === store.id ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                          }`}
                        >
                          <div className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center overflow-hidden">
                            {store.logoUrl ? (
                              <img src={store.logoUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Store className="w-3 h-3 text-gray-400" />
                            )}
                          </div>
                          <span>{store.name}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        No stores found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="h-8 w-px bg-gray-200" />

            {/* User info and sign out */}
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-8">
          <Outlet context={{ viewAsStoreId, viewAsStoreName: viewAsStore?.name }} />
        </div>
      </main>
    </div>
  );
}
